import React, { useEffect, useState, useRef } from 'react';
import { axiosInstance } from "../../utils/axios.js";
import { useParams, useNavigate } from 'react-router-dom';
import { toJstDateInputValue } from "../../utils/dateTimeJst";

const UserRescheduleContainer = (props) => {
  const { id: reservationId } = useParams();
  const navigate = useNavigate();
  
  const [reservation, setReservation] = useState(null);
  const [title, setTitle] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [appointmentType, setAppointmentType] = useState('initial');
  const [shiftsData, setShiftsData] = useState([]);
  const [error, setError] = useState(null);
  const [doctorId, setDoctorId] = useState("any");
  const [doctors, setDoctors] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [headerDates, setHeaderDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);

  const morningRef = useRef(null);
  const afternoonRef = useRef(null);
  const eveningRef = useRef(null);

  // 時間帯生成
  const generateTimeIntervals = () => {
    const intervals = [];
    for (let h = 8; h < 24; h++) {
      intervals.push({
        label: `${String(h).padStart(2, '0')}:00～`,
        start: `${String(h).padStart(2, '0')}:00`,
        end: `${String(h + 1).padStart(2, '0')}:00`
      });
    }
    return intervals;
  };

  // 連続するシフトのグループ化
  const groupConsecutiveShifts = (shifts) => {
    if (!shifts || shifts.length === 0) return [];
    const sorted = [...shifts].sort(
      (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
    let groups = [];
    let currentGroup = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const prevTime = new Date(sorted[i - 1].start_time);
      const currTime = new Date(sorted[i].start_time);
      const diffInMinutes = (currTime.getTime() - prevTime.getTime()) / (1000 * 60);
      if (diffInMinutes === 5) {
        currentGroup.push(sorted[i]);
      } else {
        groups.push(currentGroup);
        currentGroup = [sorted[i]];
      }
    }
    if (currentGroup.length) groups.push(currentGroup);
    return groups;
  };

  // 時間帯でフィルタリング
  const filterShiftsByInterval = (shifts, startTime, endTime) => {
    const startHour = parseInt(startTime.split(':')[0]);
    const endHour = parseInt(endTime.split(':')[0]);
    return shifts.filter(shift => {
      const shiftDate = new Date(shift.start_time);
      const hour = shiftDate.getHours();
      return hour >= startHour && hour < endHour;
    });
  };

  // 時刻フォーマット
  const formatTime = (dateTime) => {
    const date = new Date(dateTime);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // 分を加算
  const addMinutesToDateTime = (dateTime, minutesToAdd) => {
    const date = new Date(dateTime);
    date.setMinutes(date.getMinutes() + minutesToAdd);
    return date;
  };

  // ローカル日付フォーマット
  const formatLocalDate = (date) => {
    return toJstDateInputValue(date);
  };

  // 初期データ取得
  useEffect(() => {
    fetchReservationAndDoctors();
  }, [reservationId]);

  // ヘッダー日付生成
  useEffect(() => {
    const dates = [];
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() + weekOffset * 7);
    
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      dates.push(d);
    }
    
    setHeaderDates(dates);
    setSelectedDate(dates[0]);
  }, [weekOffset]);

  // シフトデータ取得
  useEffect(() => {
    if (selectedDate && reservation) {
      fetchShiftsData();
    }
  }, [selectedDate, doctorId, reservation]);

  const fetchReservationAndDoctors = async () => {
    try {
      setIsLoading(true);
      
      // 予約詳細取得
      const reservationResponse = await axiosInstance.get(`/api/medichat/reservations/${reservationId}`, {
        headers: { 'uid': props.token }
      });
      
      const reservationData = reservationResponse.data;
      setReservation(reservationData);
      setAppointmentType(reservationData.first ? 'initial' : 'follow_up');
      
      // 診療科目名を設定
      const subjectId = reservationData.subject_id;
      if (subjectId == 1) {
        setTitle('高血圧治療');
      } else if (subjectId == 2) {
        setTitle('痛風（高尿酸血症）');
      } else if (subjectId == 3) {
        setTitle('糖尿病');
      } else {
        setTitle('高脂血症');
      }

      // 医師一覧取得
      const doctorsResponse = await axiosInstance.get(`/api/medichat/reservations/doctors_list/${subjectId}`, {
        headers: { 'uid': props.token }
      });
      
      setDoctors(doctorsResponse.data);
      setIsLoading(false);
    } catch (error) {
      setError('データの取得に失敗しました');
      setIsLoading(false);
    }
  };

  const fetchShiftsData = async () => {
    try {
      setIsLoading(true);
      const selectedDateStr = formatLocalDate(selectedDate);
      
      const response = await axiosInstance.get(`/api/medichat/reservations/${reservation.subject_id}/shift`, {
        headers: { 'uid': props.token },
        params: {
          date: selectedDateStr,
          doctor_id: doctorId
        }
      });
      
      setShiftsData(response.data.shifts);
      setIsLoading(false);
    } catch (error) {
      setError('シフトデータの取得に失敗しました');
      setIsLoading(false);
    }
  };

  // 利用可能ブロック取得
  const getAvailableBlocks = (interval) => {
    let availableShifts = shiftsData.filter(shift => !shift.digest);
    availableShifts = filterShiftsByInterval(availableShifts, interval.start, interval.end);

    // 当日なら現在時刻+15分以降のみ
    const todayStr = formatLocalDate(new Date());
    const selectedDateStr = formatLocalDate(selectedDate);
    
    if (todayStr === selectedDateStr) {
      const threshold = new Date(Date.now() + 15 * 60 * 1000);
      availableShifts = availableShifts.filter(shift => 
        new Date(shift.start_time) >= threshold
      );
    }

    // 現在の予約時間は除外
    if (reservation) {
      availableShifts = availableShifts.filter(shift => {
        const shiftTime = new Date(shift.start_time).getTime();
        const reservationTime = new Date(reservation.start_time).getTime();
        return !(shiftTime === reservationTime && shift.admin_user_id === reservation.admin_user_id);
      });
    }

    // 医師ごとにグループ化
    const shiftsByDoctor = {};
    availableShifts.forEach(shift => {
      if (!shiftsByDoctor[shift.admin_user_id]) {
        shiftsByDoctor[shift.admin_user_id] = [];
      }
      shiftsByDoctor[shift.admin_user_id].push(shift);
    });

    let allValidGroups = [];
    const requiredShifts = appointmentType === 'initial' ? 3 : 2;

    Object.entries(shiftsByDoctor).forEach(([docId, doctorShifts]) => {
      doctorShifts.sort((a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      );
      const groups = groupConsecutiveShifts(doctorShifts);

      groups.forEach(group => {
        let i = 0;
        while (i + requiredShifts <= group.length) {
          const neededShifts = group.slice(i, i + requiredShifts);
          const formattedStartTime = formatTime(neededShifts[0].start_time);
          const endTime = formatTime(
            addMinutesToDateTime(
              neededShifts[neededShifts.length - 1].start_time,
              5
            )
          );
          const all_time = neededShifts.map(s => s.start_time);

          allValidGroups.push({
            id: neededShifts[0].id,
            startTime: formattedStartTime,
            endTime: endTime,
            start_time: all_time[0],
            shiftIds: neededShifts.map(s => s.id),
            admin_user_id: parseInt(docId, 10),
            appointmentType,
            subject_id: reservation.subject_id,
            admin_user_name: neededShifts[0].admin_user_name || 'Unknown'
          });
          i += requiredShifts;
        }
      });
    });

    // 重複潰しロジック（全医師選択時）
    if (doctorId === "any") {
      const dedupedMap = {};
      allValidGroups.forEach(block => {
        const key = `${block.startTime}-${block.endTime}`;
        const doc = doctors.find(d => d.id === block.admin_user_id);
        const rank = doc ? doc.ranking : Infinity;

        if (!dedupedMap[key] || rank < dedupedMap[key].rank) {
          dedupedMap[key] = { block, rank };
        }
      });
      allValidGroups = Object.values(dedupedMap).map(entry => entry.block);
    }

    // 時刻順に並べ替え
    allValidGroups.sort((a, b) => {
      if (a.startTime < b.startTime) return -1;
      if (a.startTime > b.startTime) return 1;
      return a.admin_user_id - b.admin_user_id;
    });

    return allValidGroups;
  };

  const scrollToSection = (ref) => {
    if (ref && ref.current) {
      const yOffset = -215;
      const elementPosition = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset + yOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleReservationChange = (block) => {
    setSelectedBlock(block);
    setShowConfirmModal(true);
  };

  const confirmReschedule = async () => {
    if (!selectedBlock) return;

    try {
      setIsLoading(true);
      
      const response = await axiosInstance.put(`/api/medichat/reservations/${reservationId}/reschedule`, {
        start_time: selectedBlock.start_time,
        admin_user_id: selectedBlock.admin_user_id,
        shift_ids: selectedBlock.shiftIds
      }, {
        headers: { 'uid': props.token }
      });

      if (response.data.success) {
        navigate('/mypage/reservation?status=reschedule_success');
      } else {
        setError(response.data.error || '予約変更に失敗しました');
      }
    } catch (error) {
      setError('予約変更に失敗しました');
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
    }
  };

  const renderWeekNavigation = () => {
    const headerMonth = selectedDate ? 
      `${selectedDate.getFullYear()}年${selectedDate.getMonth() + 1}月` : '';
    
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '6px 0px', 
        height: '35px' 
      }}>
        <button 
          className="reservation_time_header_button_1" 
          onClick={() => weekOffset > 0 && setWeekOffset(weekOffset - 1)}
          disabled={weekOffset === 0}
          style={{ fontSize: '4.0vw' }}
        >
          ← 前の週
        </button>
        <div className="reservation_time_header_date_h1" style={{ fontWeight: '500', fontSize: '1.2em' }}>
          {headerMonth}
        </div>
        <button 
          className="reservation_time_header_button_2" 
          onClick={() => setWeekOffset(weekOffset + 1)}
          style={{ fontSize: '4.0vw' }}
        >
          次の週 →
        </button>
      </div>
    );
  };

  const renderDateHeader = () => {
    const today = new Date();
    const todayStr = formatLocalDate(today);
    
    return (
      <div style={{ 
        display: 'flex', 
        borderBottom: '1px solid #F2F2F2', 
        borderTop: '1px solid #F2F2F2', 
        height: '65px' 
      }}>
        {headerDates.map((date, idx) => {
          const dateStr = formatLocalDate(date);
          let style = {
            width: '14.2%',
            textAlign: 'center',
            cursor: dateStr < todayStr ? 'not-allowed' : 'pointer',
            padding: '5px',
            opacity: dateStr < todayStr ? 0.5 : 1
          };
          
          if (formatLocalDate(date) === formatLocalDate(selectedDate)) {
            style = { ...style, backgroundColor: '#FFA500', color: 'white', fontWeight: 'bold' };
          } else if (date.getDay() === 0) {
            style = { ...style, color: 'red', fontWeight: 'normal' };
          } else if (date.getDay() === 6) {
            style = { ...style, color: 'blue', fontWeight: 'normal' };
          }
          
          return (
            <div 
              key={idx} 
              style={style} 
              onClick={() => { if (dateStr >= todayStr) setSelectedDate(date); }}
            >
              <p className="reservation_time_header_days">
                {["日", "月", "火", "水", "木", "金", "土"][date.getDay()]}
              </p>
              <p className="reservation_time_header_date">{date.getDate()}</p>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSelectionOptions = () => {
    return (
      <div style={{ padding: '16px 0px', borderBottom: '1px solid #F2F2F2' }}>
        <div style={{ height: '30px' }}>
          <label style={{ marginRight: '10px', fontWeight: 'bold', fontSize: '1.2em' }}>
            担当医師: 
          </label>
          <select 
            value={doctorId} 
            onChange={(e) => setDoctorId(e.target.value)}
            style={{ 
              padding: '5px', 
              borderRadius: '4px', 
              background: 'white', 
              fontSize: '1.1em', 
              color: 'black', 
              border: 'none', 
              borderBottom: '1px solid black' 
            }}
          >
            <option value="any">全医師</option>
            {doctors.map(doctor => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderScrollButtons = () => {
    return (
      <div style={{ 
        padding: '10px 0', 
        textAlign: 'center', 
        borderBottom: '1px solid #F2F2F2', 
        height: '25px' 
      }}>
        <button 
          className="reservation_time_header_button_2" 
          onClick={() => scrollToSection(morningRef)} 
          style={{ marginRight: '10px', fontSize: '3.5vw' }}
        >
          午前 (8-12)
        </button>
        <button 
          className="reservation_time_header_button_2" 
          onClick={() => scrollToSection(afternoonRef)} 
          style={{ marginRight: '10px', fontSize: '3.5vw' }}
        >
          午後 (12-18)
        </button>
        <button 
          className="reservation_time_header_button_2" 
          onClick={() => scrollToSection(eveningRef)} 
          style={{ fontSize: '3.5vw' }}
        >
          夜間 (18-24)
        </button>
      </div>
    );
  };

  const renderScheduleTable = () => {
    const today = new Date();
    const todayStr = toJstDateInputValue(today);
    const selectedDateStr = toJstDateInputValue(selectedDate);
    const timeIntervals = generateTimeIntervals();
    
    const displayedIntervals = (todayStr === selectedDateStr)
      ? timeIntervals.filter(interval => parseInt(interval.start.split(':')[0]) >= today.getHours())
      : timeIntervals;

    return (
      <div style={{ marginTop: '20px'}}>
        {displayedIntervals.map((interval, idx) => {
          let rowRef = null;
          const hour = parseInt(interval.start.split(":")[0]);
          if (hour === 8) rowRef = morningRef;
          else if (hour === 12) rowRef = afternoonRef;
          else if (hour === 18) rowRef = eveningRef;
          
          const availableBlocks = getAvailableBlocks(interval);
          
          return (
            <div 
              key={idx} 
              ref={rowRef} 
              style={{ 
                display: 'flex', 
                borderBottom: '1px solid #eee', 
                padding: '10px 0', 
                alignItems: 'center' 
              }}
            >
              <div style={{ width: '20%', fontWeight: 'bold' }}>
                {interval.label}
              </div>
              <div style={{ flex: 1, padding: '5px' }}>
                {availableBlocks.length > 0 ? (
                  <div className="reservation_time_container">
                    {availableBlocks.map((block, i) => (
                      <div 
                        key={i}
                        className="reservation_time_container_available"
                        style={{ 
                          cursor: 'pointer', 
                          margin: '4px', 
                          padding: '4px',
                          borderRadius: '4px',
                          display: 'inline-block',
                          fontWeight: 'bold'
                        }}
                        onClick={() => handleReservationChange(block)}
                      >
                        <span style={{ color: 'red', fontWeight: 'bold' }}>◎</span>
                        {block.startTime} 〜 {block.endTime}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    color: 'black', 
                    textAlign: 'center', 
                    width: '95%', 
                    background: 'rgb(242, 242, 242)', 
                    padding: '6px 0px' 
                  }}>
                    ×
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const DateTimeComponent = ({ isoDate }) => {
    const date = new Date(isoDate);
    
    const formattedDate = date.toLocaleDateString('ja-JP', {
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
    });
    
    const formattedTime = date.toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit',
    });
    
    return (
        <div>
        <p>{`${formattedDate} ${formattedTime}`}~</p>
        </div>
    );
  };

  if (isLoading && !reservation) {
    return (
      <div>
        <div className="loader" style={{ width: '100px', height: '100px' }}></div>
        <div
          style={{
            width: '100%',
            position: 'absolute',
            bottom: '0%',
            height: '80px',
            background: '#FEFBF9',
            zIndex: '12',
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>{error}</p>
        <button onClick={() => navigate('/mypage/reservations')}>
          予約一覧に戻る
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', margin: 'auto', overflowX: 'scroll', marginBottom: '100px' }}>
      
      <div className="reservation_time_header" style={{background:'white',height:'auto'}}>
      <div style={{borderBottom:'1px solid lightgrey',margin:'0px',background:'white',height:'63px'}}>
        <h2 style={{fontSize:'5vw',margin:'0px',padding:'1rem',color:'brown'}}>予約変更 - {title}</h2>
      </div>
        {renderSelectionOptions()}
        {renderWeekNavigation()}
        {renderDateHeader()}
        {renderScrollButtons()}
      </div>
      
      <div style={{ paddingTop: '278px' }} className="hoge">
        {renderScheduleTable()}
      </div>
{/* 確認モーダル - 洗練バージョン */}
{showConfirmModal && selectedBlock && reservation && (
  <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <span className="close-button" onClick={() => setShowConfirmModal(false)}>✖︎</span>
      
      {/* タイトル */}
      <div className="reschedule_modal_title">
        予約時間を変更しますか？
      </div>
      
      {/* 変更内容 */}
      <div className="reschedule_modal_content">
        {/* 変更前セクション */}
        <div className="reschedule_modal_section">
          <div className="reschedule_modal_section_title before">
            【変更前】
          </div>
          <div className="reschedule_modal_section_details">
            <p><DateTimeComponent isoDate={reservation.start_time} /></p>
            <p>担当医: {reservation.admin_user.name}先生 / {reservation.first ? '初診' : '再診'}</p>
          </div>
        </div>
        
        {/* 矢印 */}
        <div className="reschedule_modal_arrow">
          ↓
        </div>
        
        {/* 変更後セクション */}
        <div className="reschedule_modal_section">
          <div className="reschedule_modal_section_title after">
            【変更後】
          </div>
          <div className="reschedule_modal_section_details">
            <p><DateTimeComponent isoDate={selectedBlock.start_time} /></p>
            <p>担当医: {selectedBlock.admin_user_name}先生 / {reservation.first ? '初診' : '再診'}</p>
          </div>
        </div>
      </div>
      
      {/* 注意事項 */}
      <div className="reschedule_modal_notice">
        ※予約変更は開始3時間前まで可能です。
      </div>
      
      {/* ボタン */}
      <div className="multi_button">
        <button 
          className="multi_button_right" 
          onClick={confirmReschedule}
          disabled={isLoading}
          style={{ fontSize: '4.0vw', padding: '12px 0px', fontWeight:'bold' }}
        >
          {isLoading ? '変更中...' : '変更する'}
        </button>
        <button 
          className="multi_button_left" 
          onClick={() => setShowConfirmModal(false)}
          style={{ fontSize: '4.0vw', padding: '12px 0px' }}
        >
          キャンセル
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default UserRescheduleContainer;